import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UseFormGetValues } from "react-hook-form";
import { format } from "date-fns";

import useFetch from "../useFetch";
import { ToastContext } from "src/context/toastContext";

import { ToastTypes } from "src/types";
import { AddCaseResponseProps } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import { AddCaseProps } from "src/pages/addCase/types";
import { CaseContext } from "src/context/caseContext";
import { AuthContext } from "src/context/authContext";



const useCaseCreateCall = (getValues: UseFormGetValues<AddCaseProps>) => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [newCaseId, updateNewCaseId] = useState<number>();

  const navigate = useNavigate()
  const { showToast } = useContext(ToastContext)
  const { draftCaseId } = useContext(CaseContext)
  const { userData, updateUserData } = useContext(AuthContext)





  const { mutate: uploadCaseFile, data: uploadCaseData } = useFetch(APIEndpoints.UPLOAD_CASE_FILE)

  useEffect(() => {
   if (uploadCaseData) {
      if (newCaseId)
        navigate(`/cases/${newCaseId}/documents/new`);
      else
        navigate('/cases')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCaseData])





  /**
   * @AddNewCase form handling for case data submission
   * @fourth case submit handler
   */
  const { loading: addCaseLoading, data: addCaseData, mutate: addNewCase } =
    useFetch<AddCaseResponseProps>(APIEndpoints.ADD_NEW_CASE);

  useEffect(() => {
    if (addCaseData && addCaseData.status === 200) {
      localStorage.removeItem('newCase');
      showToast([`Case Added Successfully!`], ToastTypes.SUCCESS);
      if (draftCaseId !== 0 && userData) {
        updateUserData({ ...userData, draft_count: userData.draft_count - 1 })
      }

      const docs = getValues("uploaded_documents");
      if (docs && docs.length > 0) {
        updateNewCaseId(addCaseData.case_id || 0)
        const formData = new FormData();
        formData.append("file", docs[0]);

        setIsUploading(true); // Start upload

        uploadCaseFile(formData, { caseId: addCaseData.case_id }, {
          onSuccess: () => {
            setIsUploading(false); // Stop upload
            showToast([`Your request submitted successfully!`], ToastTypes.SUCCESS);
            //navigate('/cases')
            navigate(`/cases/${addCaseData.case_id}/documents/new`);

          },
          onError: () => {
            setIsUploading(false); // Stop upload in case of error
            showToast(["File upload failed. Please try again."], ToastTypes.ERROR);
          },
        });
      } else {
        showToast([`Your request submitted successfully!`], ToastTypes.SUCCESS);
        //navigate('/cases')
        navigate(`/cases/${addCaseData.case_id}/documents/new`);

      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCaseData]);

  /** @fourth */
  const caseDataSubmission = () => {
    const values = getValues();
        //console.log("values.....",values)

    const prior_treatment_reason_attributes: any[] = [];
    const prior_treatment_applied_attributes: any[] = [];
    const prior_treatment_attributes: { drug_name: string; failure_reason: undefined; date_tried: undefined; }[] = [];
    const prescription_attributes_attributes = [];

    if (values.prescription_attributes.condition) {
      prescription_attributes_attributes.push({
        name: "condition",
        value: values.prescription_attributes.condition
      });
    }
    // if (values.prescription_attributes.quickstart) {
    //   prescription_attributes_attributes.push({
    //     name: "quickstart",
    //     value: values.prescription_attributes.quickstart,
    //   })
    // }
    if (values.prescription_attributes.Patient_training_required !== undefined) {
      prescription_attributes_attributes.push({
        name: "patient_training_required",
        value: values.prescription_attributes.Patient_training_required,
      })
    }
    if (values.prescription_attributes.targetAfterMealBloodGlucoseMin) {
      prescription_attributes_attributes.push({
        name: "target_after_meal_blood_glucose_min",
        value: values.prescription_attributes.targetAfterMealBloodGlucoseMin,
      })
    }
    if (values.prescription_attributes.targetAfterMealBloodGlucoseMax) {
      prescription_attributes_attributes.push({
        name: "target_after_meal_blood_glucose_max",
        value: values.prescription_attributes.targetAfterMealBloodGlucoseMax,
      })
    }
     if (values.prescription_attributes.patient_consent) {
      prescription_attributes_attributes.push({
        name: "patient_consent",
        value: values.prescription_attributes.patient_consent,
      })}
      if (values.prescription_attributes.number_of_boxes) {
      prescription_attributes_attributes.push({
        name: "number_of_boxes",
        value: values.prescription_attributes.number_of_boxes,
      })
    }


    const applicable =
      values.prescription_information_attributes?.applicable_to_patient || [];
    applicable.forEach((reason) => {
      prior_treatment_applied_attributes.push({
        reason,
        sub_reasons: [],
      });
    });

    const reasonsObj: {
      main?: string[];
      needle_phobia?: string[];
    } =
      values.prescription_information_attributes
        ?.reasons_alternative_insulins_not_effective || {};


    const mainReasons = reasonsObj.main || [];
    const needlePhobiaSubReasons = reasonsObj.needle_phobia || [];

    mainReasons.forEach((reason: string) => {
      // 👇 special handling for Needle Phobia
      if (reason === "Needle Phobia and unable to self-inject") {
        prior_treatment_reason_attributes.push({
          reason: reason, // string (as per your requirement)
          sub_reasons: needlePhobiaSubReasons,
        });
      } else {
        prior_treatment_reason_attributes.push({
          reason: reason, 
          sub_reasons: [],
        });
      }
    });


    if (values.prescription_information_attributes.cp_diuretics) {
      if (values.prescription_information_attributes.cp_diuretics.diuretics_tried) {
        const meds = [
          "other",
          "novolog",
          "novolin",
          "humalog",
          "humulin",
          "fiasp",
          "lyumjev",
          "aspart",
          "lispro",
          "apidra",
          "admelog",
          "u200",
          "u500",
          "two_orals_anti_diabetics",
        ];
        const basePath = "prescription_information_attributes.cp_diuretics.diuretics_tried";
        const allValues = getValues(basePath);

        meds.forEach((med) => {
          const isSelected = allValues[med as keyof typeof allValues];
          if (!isSelected) return;
          const props = allValues?.[`${med}_props` as keyof typeof allValues];
          const failure_reason = props?.[`${med}_failure_reason` as keyof typeof props];
          const failure_reason_other = props?.[`${med}_failure_reason_other` as keyof typeof props];
          const date_tried = props?.[`${med}_tried_date` as keyof typeof props];
          const other_name = med === "other" ? props?.["other_name" as keyof typeof props] : undefined;

          prior_treatment_attributes.push({
            drug_name: med === "other" ? ((other_name as unknown as string) ?? med) : med,
            failure_reason: failure_reason === "Other" ? failure_reason_other : failure_reason,
            date_tried,
          });
        });
      }
    }
    const { condition,Patient_training_required,targetAfterMealBloodGlucoseMax,targetAfterMealBloodGlucoseMin,patient_consent, ...actualPrescription } = values.prescription_attributes;

    const shipments_attributes = [];

    shipments_attributes.push({
      ship_to: "other",
      practice_id: values.doctor_attributes.practice_id

    });

    const patient_attributes_attributes = []
    if (values.patient_attributes.weight && values.patient_attributes.weight !== "0") {
      patient_attributes_attributes.push({
        name: "patient_weight",
        value: values.patient_attributes.weight
      })
    }
    const { weight, ...actual_patient_attributes } = values.patient_attributes
    const { cp_diuretics, applicable_to_patient, reasons_alternative_insulins_not_effective, ...actualPrescriptionInformation } = values.prescription_information_attributes;

    const newValues = {
      ...values,
      category: "rx",
      drug_name: values.prescription_attributes.drug_name,
      prescription_attributes: {
        ...actualPrescription,
        diagnosis_code: values.prescription_attributes.diagnosis_code.join(","),
        is_daw: true,
        is_speciality_pharmacy_only: 0,
        written_date: format(values.prescription_attributes.written_date, "dd/MM/yyyy"),
        refills_authorized: actualPrescriptionInformation.refills,
        prescription_attributes_attributes,
      },
      prescription_information_attributes: {
        ...actualPrescriptionInformation,
        inhale_units: actualPrescriptionInformation.min_inhale_units || "",
      },
      prior_treatment_attributes: prior_treatment_attributes.length > 0 ? prior_treatment_attributes : [],
      prior_treatment_reason_attributes: prior_treatment_reason_attributes.length > 0 ? prior_treatment_reason_attributes : [],
      prior_treatment_applied_attributes: prior_treatment_applied_attributes.length > 0 ? prior_treatment_applied_attributes : [],
      shipments_attributes,
      patient_attributes: {
        ...actual_patient_attributes,
        ...(patient_attributes_attributes.length > 0 ? { patient_attributes_attributes } : {})
      },
      patient_firstlastname: `${values.patient_attributes.firstname} ${values.patient_attributes.lastname}`,
      patient_fullname: `${values.patient_attributes.firstname}${`${values.patient_attributes.middlename ? ` ${values.patient_attributes.middlename}` : ""}`} ${values.patient_attributes.lastname}`,
      patient_dob: values.patient_attributes.dob,
      insurances_attributes: values.insurances_attributes?.map((insurance, index) => {
        insurance.insurance_priority = index;
        return insurance;
      }),
      case_attributes_attributes: [
        {
          name: "soc",
          value: "other"
        },
        ...(values.case_attributes && values.case_attributes.pa_approved ? [{
          name: "pa_approved",
          value: values.case_attributes.pa_approved,
        }] : []),
        ...(values.case_attributes && values.case_attributes.pa_approved === "pa_approved" ? [{
          name: "pa_approved_date",
          value: values.case_attributes.pa_approved_date,
        }] : []),
      ],
    };

    const { case_attributes, api_pa_approved, uploaded_documents, original_id, ...actualCaseValues } = newValues;

    addNewCase({
      case: actualCaseValues,
      ...(draftCaseId && draftCaseId !== 0 ? { draft_id: draftCaseId } : {})
    });
    // console.log("actual Case Values ", actualCaseValues)
  };

  return { addCaseLoading, isUploading, caseDataSubmission };
}

export default useCaseCreateCall;
